import { useState, useEffect } from "react";
import { useGeolocationLogs } from "@/hooks/useGeolocationLogs";
import { useGeolocationSettings } from "@/hooks/useGeolocationSettings";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Download, ChevronLeft, ChevronRight, Filter, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

export function GeolocationLogsTable() {
  const { currentCompany } = useAuth();
  const { geofences } = useGeolocationSettings();
  const {
    logs,
    isLoading,
    totalCount,
    currentPage,
    totalPages,
    filters,
    setFilters,
    handlePageChange,
    exportToCSV,
  } = useGeolocationLogs();

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const fetchProfiles = async () => {
      if (!currentCompany?.id) return;
      
      const { data } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .eq("company_id", currentCompany.id)
        .order("first_name");
      
      if (data) setProfiles(data);
    };
    
    fetchProfiles();
  }, [currentCompany?.id]);

  const formatDistance = (meters: number | null) => {
    if (meters === null) return 'N/A';
    if (meters < 1000) return `${Math.round(meters)}m`;
    return `${(meters / 1000).toFixed(1)}km`;
  };

  return (
    <div className="space-y-4">
      {/* Filters Section */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="h-4 w-4 mr-2" />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={exportToCSV}
          disabled={logs.length === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          Export to CSV
        </Button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 border rounded-lg bg-muted/50">
          <div className="space-y-2">
            <Label htmlFor="start-date">Start Date</Label>
            <Input
              id="start-date"
              type="date"
              value={filters.startDate || ''}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value || undefined })}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="end-date">End Date</Label>
            <Input
              id="end-date"
              type="date"
              value={filters.endDate || ''}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value || undefined })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="employee">Employee</Label>
            <Select
              value={filters.profileId || 'all'}
              onValueChange={(value) => 
                setFilters({ ...filters, profileId: value === 'all' ? undefined : value })
              }
            >
              <SelectTrigger id="employee">
                <SelectValue placeholder="All employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {profiles.map((profile) => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {`${profile.first_name || ''} ${profile.last_name || ''}`.trim()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="geofence">Location</Label>
            <Select
              value={filters.geofenceId || 'all'}
              onValueChange={(value) => 
                setFilters({ ...filters, geofenceId: value === 'all' ? undefined : value })
              }
            >
              <SelectTrigger id="geofence">
                <SelectValue placeholder="All locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All locations</SelectItem>
                {geofences.map((geofence) => (
                  <SelectItem key={geofence.id} value={geofence.id}>
                    {geofence.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="radius-status">Status</Label>
            <Select
              value={
                filters.withinRadius === undefined || filters.withinRadius === null
                  ? 'all'
                  : filters.withinRadius
                  ? 'within'
                  : 'outside'
              }
              onValueChange={(value) => {
                if (value === 'all') {
                  setFilters({ ...filters, withinRadius: undefined });
                } else {
                  setFilters({ ...filters, withinRadius: value === 'within' });
                }
              }}
            >
              <SelectTrigger id="radius-status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="within">Within radius</SelectItem>
                <SelectItem value="outside">Outside radius</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No geolocation logs found
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Distance</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow
                    key={log.id}
                    className={cn(
                      log.is_within_radius === false && "bg-destructive/10"
                    )}
                  >
                    <TableCell className="font-medium">
                      {log.employee_name || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={log.event_type === 'CLOCK_IN' ? 'default' : 'secondary'}>
                        {log.event_type === 'CLOCK_IN' ? 'Clock In' : 'Clock Out'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(log.captured_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{log.geofence_label || 'N/A'}</TableCell>
                    <TableCell>{formatDistance(log.distance_from_site_meters)}</TableCell>
                    <TableCell>
                      {log.is_within_radius === null ? (
                        <Badge variant="outline">N/A</Badge>
                      ) : log.is_within_radius ? (
                        <Badge variant="default" className="bg-green-600">
                          ✓ Within radius
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          ⚠ Outside radius
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {logs.length} of {totalCount} logs
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="text-sm">
                Page {currentPage} of {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
